import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import Swal from "sweetalert2";
import { fetchSuppliers } from "../../redux/slices/supplierSlice";
import { fetchBanks } from "../../redux/slices/bankSlice";
import { createSupplierPaymentThunk } from "../../redux/slices/financeSlice";
import { getSupplierPendingBills } from "../../api/financeService";
import { FINANCE_PAYMENT_MODES as PAYMENT_MODES } from "../../constants/paymentModes";
import { getApiErrorMessage } from "../../utils/apiError";

const today = () => new Date().toISOString().split("T")[0];
const fmt = (v) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });

const SupplierPaymentModal = ({ show, onClose }) => {
  const dispatch = useDispatch();
  const suppliers = useSelector((s) => s.suppliers.list);
  const banks = useSelector((s) => s.bank.list);

  const [supplierId, setSupplierId] = useState("");
  const [paymentDate, setPaymentDate] = useState(today());
  const [paymentMode, setPaymentMode] = useState("Cash");
  const [bankId, setBankId] = useState("");
  const [referenceNo, setReferenceNo] = useState("");
  const [remarks, setRemarks] = useState("");
  const [pendingBills, setPendingBills] = useState([]);
  const [allocations, setAllocations] = useState({});
  const [loadingBills, setLoadingBills] = useState(false);
  const [saving, setSaving] = useState(false);

  const supplierOptions = suppliers
    .filter((s) => s.status === "Active")
    .map((s) => ({ value: s.supplierID || s.id, label: s.supplierName }));
  const bankOptions = banks.map((b) => ({ value: b.bankID || b.id, label: b.bankName }));

  useEffect(() => {
    dispatch(fetchSuppliers());
    dispatch(fetchBanks());
  }, [dispatch]);

  useEffect(() => {
    if (!show) {
      setSupplierId(""); setPendingBills([]); setAllocations({});
      setPaymentDate(today()); setPaymentMode("Cash"); setBankId(""); setReferenceNo(""); setRemarks("");
    }
  }, [show]);

  // Purchase records identify themselves as `purchaseID` (confirmed live), never a bare
  // `id` — pending-bills is a purchase-shaped list, so key off that, not bill.id.
  const billId = (b) => b.purchaseID ?? b.purchaseId ?? b.id;

  const handleSupplierChange = async (opt) => {
    setSupplierId(opt?.value || "");
    setPendingBills([]);
    setAllocations({});
    if (!opt?.value) return;
    setLoadingBills(true);
    try {
      const res = await getSupplierPendingBills(opt.value);
      const bills = res.data || [];
      setPendingBills(bills);
      const alloc = {};
      bills.forEach((b) => { alloc[billId(b)] = 0; });
      setAllocations(alloc);
    } catch (err) {
      Swal.fire("Error", getApiErrorMessage(err, "Failed to load pending bills."), "error");
    } finally {
      setLoadingBills(false);
    }
  };

  const setAlloc = (billIdVal, value) => {
    setAllocations((prev) => ({ ...prev, [billIdVal]: value }));
  };

  const totalAllocated = Object.values(allocations).reduce((s, v) => s + Number(v || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!supplierId) { Swal.fire("Required", "Please select a supplier.", "warning"); return; }
    if (totalAllocated <= 0) { Swal.fire("Required", "Allocate payment to at least one bill.", "warning"); return; }
    // SupplierBillPaymentDetails.BalanceAmount is NOT NULL on the backend and isn't
    // computed server-side — it must be sent as the bill's remaining balance after this
    // payment (bill balance before payment minus what's being paid now), or the insert
    // fails outright ("Cannot insert the value NULL into column 'BalanceAmount'...").
    const billAllocations = pendingBills
      .filter((b) => Number(allocations[billId(b)] || 0) > 0)
      .map((b) => {
        const payingNow = Number(allocations[billId(b)]);
        return {
          purchaseId: billId(b),
          amount: payingNow,
          balanceAmount: Math.max(0, Number(b.balanceAmount || 0) - payingNow),
        };
      });
    const payload = { supplierId, paymentDate, paymentMode, bankId, referenceNo, remarks, totalAmount: totalAllocated, billAllocations };
    setSaving(true);
    try {
      await dispatch(createSupplierPaymentThunk(payload)).unwrap();
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Payment recorded", showConfirmButton: false, timer: 2000 });
      onClose();
    } catch (err) {
      Swal.fire("Error", getApiErrorMessage(err, "Failed to record payment."), "error");
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header border-0 custom-modal-header">
              <div className="page-title">
                <h4>Supplier Bill-wise Payment</h4>
              </div>
              <button type="button" className="close" onClick={onClose}><span>×</span></button>
            </div>
            <div className="modal-body custom-modal-body">
              <form onSubmit={handleSubmit}>
                <div className="row mb-3">
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Supplier <span className="text-danger">*</span></label>
                    <Select
                      classNamePrefix="react-select"
                      options={supplierOptions}
                      value={supplierOptions.find((o) => o.value === supplierId) || null}
                      onChange={handleSupplierChange}
                      placeholder="Select supplier"
                    />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Payment Date</label>
                    <input type="date" className="form-control" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Payment Mode</label>
                    <Select
                      classNamePrefix="react-select"
                      options={PAYMENT_MODES}
                      value={PAYMENT_MODES.find((o) => o.value === paymentMode) || null}
                      onChange={(opt) => setPaymentMode(opt?.value || "Cash")}
                    />
                  </div>
                  {paymentMode !== "Cash" && (
                    <div className="col-lg-6 mb-3">
                      <label className="form-label">Bank</label>
                      <Select
                        classNamePrefix="react-select"
                        options={bankOptions}
                        value={bankOptions.find((o) => o.value === bankId) || null}
                        onChange={(opt) => setBankId(opt?.value || "")}
                        placeholder="Select bank"
                        isClearable
                      />
                    </div>
                  )}
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Reference No.</label>
                    <input className="form-control" value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} placeholder="UTR / Txn Id" />
                  </div>
                  <div className="col-lg-12 mb-2">
                    <label className="form-label">Remarks</label>
                    <input className="form-control" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
                  </div>
                </div>

                {supplierId && (
                  <>
                    <h6 className="mb-2">Pending Bills</h6>
                    {loadingBills ? (
                      <p className="text-muted">Loading bills...</p>
                    ) : pendingBills.length === 0 ? (
                      <p className="text-muted">No pending bills for this supplier.</p>
                    ) : (
                      <div className="table-responsive mb-3">
                        <table className="table table-sm table-bordered">
                          <thead className="table-light">
                            <tr>
                              <th>Bill / PO Ref</th>
                              <th>Date</th>
                              <th className="text-end">Bill Amount</th>
                              <th className="text-end">Balance</th>
                              <th style={{ width: 130 }} className="text-end">Pay Now (₹)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pendingBills.map((bill) => (
                              <tr key={billId(bill)}>
                                <td>{bill.purchaseNo || bill.referenceNumber || billId(bill)}</td>
                                <td>{bill.purchaseDate || bill.date}</td>
                                <td className="text-end">₹{fmt(bill.totalAmount)}</td>
                                <td className="text-end text-danger fw-semibold">₹{fmt(bill.balanceAmount)}</td>
                                <td>
                                  <input
                                    type="number"
                                    className="form-control form-control-sm text-end"
                                    min={0}
                                    max={bill.balanceAmount}
                                    step="0.01"
                                    value={allocations[billId(bill)] || ""}
                                    onChange={(e) => setAlloc(billId(bill), e.target.value)}
                                    placeholder="0.00"
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="table-light">
                              <td colSpan={4} className="text-end fw-bold">Total Payment</td>
                              <td className="text-end fw-bold">₹{fmt(totalAllocated)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </>
                )}

                <div className="modal-footer-btn">
                  <button type="button" className="btn btn-cancel me-2" onClick={onClose}>Cancel</button>
                  <button type="submit" className="btn btn-submit" disabled={saving || totalAllocated <= 0}>
                    {saving ? "Saving..." : `Pay ₹${fmt(totalAllocated)}`}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose} />
    </>
  );
};

export default SupplierPaymentModal;
